import React, { createContext, useMemo, useState } from 'react';

import { Button, Col, Row, Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faFilter, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { useDebounce } from 'use-debounce';
import { useHistory } from 'react-router-dom';

import {
  ALL_PRODUCT_LISTS,
  BRANDS,
  BRANDS_DELETE_UNLINKED,
  BRANDS_DOWNLOAD_CSV,
  BRANDS_UNVERIFY,
} from 'lib/networking/endpoints';
import { areYouSure } from 'lib/utils/toast';
import {
  backgroundGrey5,
  blackberry,
  bootstrapDanger,
  coolGrey,
  grape,
  greyStroke,
  purple1,
  squidInk,
  white,
} from 'lib/css/colors';
import ActiveFilterChips from 'components/shared/ActiveFilterChips';
import BrandEditModal from 'components/manage_brands/BrandEditModal';
import { BrandVerificationStatusOptions } from 'lib/enums/BrandVerificationStatus';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import ManageBrandsTable from 'components/manage_brands/ManageBrandsTable';
import MergeBrandsModal from 'components/manage_brands/MergeBrandsModal';
import BrandFilters from 'components/manage_brands/filters/BrandFilters';
import BrandImportModal from 'components/manage_brands/BrandImportModal';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchBar from 'components/shared/SearchBar';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import useQueryString from 'lib/hooks/useQueryString';
import usePost from 'lib/hooks/usePost';

const ManageBrandsContext = createContext({});

function ManageBrandsView() {
  const history = useHistory();
  const verificationStatus = useQueryString().get('verificationStatus');
  const isDPOnly = useQueryString().get('isDPOnly');
  const initialQuery = useQueryString().get('query') || '';

  const [mergeOrDeleteList, setMergeOrDeleteList] = useState([]);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [searchQuery] = useDebounce(searchInput, 500);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [productList, setProductList] = useState();
  const [status, setStatus] = useState(verificationStatus || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [showBrandImportModal, setShowBrandImportModal] = useState(false);
  const [manufacturer, setManufacturer] = useState();
  const [brandSources, setBrandSources] = useState([]);
  const [withoutOrphanBrands, setWithoutOrphanBrands] = useState(true);
  const [brandEditModalSettings, setBrandEditModalSettings] = useState({ show: false });

  const {
    data: { brands, count } = {},
    loading,
    refetch,
  } = useGet(BRANDS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    query: searchQuery,
    product_list_id: productList?.id,
    status: status,
    manufacturer_id: manufacturer?.value,
    is_dp_only: isDPOnly || false,
    brand_sources: brandSources?.map(s => s.value).join(','),
    without_orphan_brands: withoutOrphanBrands,
  });

  const { postData: deleteBrands, loading: brandsDeleting } = usePost(
    BRANDS_DELETE_UNLINKED,
    () => {
      SwalDialog('success', 'Successfully deleted brands', 'Success', 'center');
      refetch();
      setMergeOrDeleteList([]);
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while deleting brands: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const { postData: unverifyBrands, loading: brandsUnverifying } = usePost(
    BRANDS_UNVERIFY,
    () => {
      SwalDialog('success', 'Successfully unverified brands', 'Success', 'center');
      refetch();
      setMergeOrDeleteList([]);
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while unverifying brands: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const { postData: exportBrandDetails, loading: brandsExporting } = usePost(
    BRANDS_DOWNLOAD_CSV,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while.
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export the file',
      }),
  );

  const { data: { product_lists = [] } = {} } = useGet(ALL_PRODUCT_LISTS);

  const productListOptions = useMemo(() => {
    return product_lists.map((list, index) => ({ label: list.name, value: index }));
  }, [product_lists]);

  const activeFilters = [
    productList && {
      label: 'Product List',
      value: productList.name,
      onRemove: () => setProductList(undefined),
    },
    status && {
      label: 'Status',
      value:
        BrandVerificationStatusOptions.find(option => option.value === status)?.label || status,
      onRemove: () => setStatus(''),
    },
    manufacturer && {
      label: 'Manufacturer',
      value: manufacturer.label,
      onRemove: () => setManufacturer(null),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setProductList(undefined);
    setStatus('');
    setManufacturer(null);
  };

  return (
    <ManageBrandsContext.Provider
      value={{ mergeList: mergeOrDeleteList, setMergeList: setMergeOrDeleteList }}
    >
      <div className={css(styles.pageContainer)}>
        {/* Header Section */}
        <div className={css(styles.headerSection)}>
          <div className={css(styles.headerContent)}>
            <h1 className={css(styles.pageTitle)}>Manage Brands</h1>
            <div className={css(styles.headerActions)}>
              <Button
                className={css(styles.qaButton)}
                onClick={() => history.push('/enrich-products/manage-brands/brand-stats')}
              >
                Brand Stats
              </Button>
              <Button
                className={css(styles.qaButton)}
                onClick={() => history.push('/enrich-products/manage-brands/brand-content-qa')}
              >
                Brand Content QA
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Container className={css(styles.mainContent)}>
          <Row className={css(styles.toolbarRow)}>
            <Col xs={5} className="d-flex align-items-center">
              <div className={css(styles.searchWrapper)}>
                <SearchBar
                  value={searchInput}
                  setValue={setSearchInput}
                  placeholder="Search by brand name, manufacturer"
                />
              </div>
              <Button
                className={css(styles.filterButton)}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Col>
            <Col xs={7} className={css(styles.actionButtonsCol)}>
              <Button
                className={css(styles.dangerButton)}
                onClick={() =>
                  areYouSure(
                    () => deleteBrands({ deleteIds: mergeOrDeleteList.map(brand => brand.id) }),
                    'Are you sure you want to delete the selected brands?',
                  )
                }
                disabled={mergeOrDeleteList.length < 1 || brandsDeleting}
              >
                {brandsDeleting ? (
                  <Spinner animation="border" role="status" size="sm" />
                ) : (
                  'Delete Unlinked Brands'
                )}
              </Button>
              <Button
                className={css(styles.outlineDangerButton)}
                onClick={() =>
                  areYouSure(
                    () => unverifyBrands({ brand_ids: mergeOrDeleteList.map(brand => brand.id) }),
                    'Are you sure you want to unverify the selected brands?',
                  )
                }
                disabled={mergeOrDeleteList.length < 1 || brandsUnverifying}
              >
                {brandsUnverifying ? (
                  <Spinner animation="border" role="status" size="sm" />
                ) : (
                  'Unverify Selected Brands'
                )}
              </Button>
              <Button
                className={css(styles.secondaryButton)}
                onClick={() => setShowMergeModal(true)}
                disabled={mergeOrDeleteList.length < 2}
              >
                Merge Brands
              </Button>
              <Button
                className={css(styles.primaryButton)}
                onClick={() => history.push('/enrich-products/create-brand')}
              >
                + New Brand
              </Button>
              <Button
                className={css(styles.primaryButton)}
                onClick={() => setShowBrandImportModal(true)}
              >
                Import New Brands
              </Button>
              <Button
                className={css(styles.outlineButton)}
                onClick={() =>
                  exportBrandDetails({
                    query: searchQuery,
                    product_list_id: productList?.id,
                    status: status,
                    manufacturer_id: manufacturer?.value,
                    brand_sources: brandSources?.map(s => s.value).join(','),
                    without_orphan_brands: withoutOrphanBrands,
                  })
                }
                disabled={brandsExporting}
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Export
              </Button>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex align-items-center flex-wrap mt-3">
              <span className={classNames(css(styles.selectedText), 'text-secondary ml-2 mr-3')}>
                {count} Brands found
              </span>
              <ActiveFilterChips filters={activeFilters} onClearAll={clearAllFilters} />
            </Col>
            <Col className={classNames(css(styles.pagination), 'd-flex justify-content-end')}>
              <PaginationComponent
                totalItems={count}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                defaultSize
              />
            </Col>
          </Row>
          <div className={css(styles.scrollableContainer)}>
            {loading ? (
              <LoadingSpinner short />
            ) : (
              <ManageBrandsTable
                brands={brands}
                setBrandEditModalSettings={setBrandEditModalSettings}
                refetch={refetch}
              />
            )}
          </div>
          {showFilters && (
            <div className={css(styles.brandFiltersOverlay)}>
              <BrandFilters
                productList={productList}
                setProductList={setProductList}
                status={status}
                setStatus={setStatus}
                setShowFilters={setShowFilters}
                loading={loading}
                manufacturer={manufacturer}
                setManufacturer={setManufacturer}
                product_lists={product_lists}
                productListOptions={productListOptions}
                brandSources={brandSources}
                setBrandSources={setBrandSources}
                withoutOrphanBrands={withoutOrphanBrands}
                setWithoutOrphanBrands={setWithoutOrphanBrands}
              />
            </div>
          )}
          {showMergeModal ? (
            <MergeBrandsModal
              mergeList={mergeOrDeleteList}
              setMergeList={setMergeOrDeleteList}
              onHide={() => {
                setShowMergeModal(false);
                refetch();
                setMergeOrDeleteList([]);
              }}
            />
          ) : showBrandImportModal ? (
            <BrandImportModal onHide={() => setShowBrandImportModal(false)} refetch={refetch} />
          ) : null}
          {brandEditModalSettings.show ? (
            <BrandEditModal
              show={brandEditModalSettings.show}
              brand={brandEditModalSettings.brand}
              onHide={() => {
                setBrandEditModalSettings({ show: false });
                setMergeOrDeleteList([]);
              }}
              refetch={refetch}
            />
          ) : null}
        </Container>
      </div>
    </ManageBrandsContext.Provider>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  qaButton: {
    backgroundColor: white,
    color: blackberry,
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
      color: white,
    },
  },
  mainContent: {
    padding: '24px 40px',
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    maxWidth: 300,
  },
  filterButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 500,
    marginLeft: 12,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: `${grape}15`,
      borderColor: grape,
    },
  },
  actionButtonsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
    },
  },
  secondaryButton: {
    backgroundColor: greyStroke,
    color: squidInk,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: coolGrey,
      color: white,
    },
  },
  outlineButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `2px solid ${grape}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
      color: white,
    },
  },
  dangerButton: {
    backgroundColor: bootstrapDanger,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      opacity: 0.85,
    },
  },
  outlineDangerButton: {
    backgroundColor: 'transparent',
    color: bootstrapDanger,
    border: `2px solid ${bootstrapDanger}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: bootstrapDanger,
      color: white,
    },
  },
  scrollableContainer: {
    height: '70vh',
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflow: 'auto',
  },
  brandFiltersOverlay: {
    position: 'absolute',
    padding: '1.5rem',
    top: 40,
    right: 0,
    bottom: 0,
    width: '30vw',
    minHeight: '100vh',
    backgroundColor: white,
    borderLeft: `4px solid ${grape}`,
    transitionProperty: 'transform, visibility, width',
    transitionDuration: '0.4s',
    transitionTimingFunction: 'ease-in-out',
    zIndex: 999,
    visibility: 'visible',
  },
  pagination: {
    marginTop: '1rem',
    zIndex: 2,
  },
  selectedText: {
    fontSize: '1rem',
    color: coolGrey,
  },
});

export default ManageBrandsView;
export { ManageBrandsContext };
